// The evidence quality review beside a document's tags (backend lib/evidenceQuality.ts): rule checks
// on what was filed (readable text, draft markings, sign-off, dates against the period it should cover)
// and, at the AI tier, whether the document covers each tagged control fully, partly or not at all.
// Advice only — it never removes a tag or changes a status — and a person may dismiss it.

import { useState } from "react";
import type React from "react";
import { api, apiErrorMessage } from "../api/client";
import { theme, btnSecondary, inputStyle } from "../theme";
import { StatusTag, type StatusTone } from "./Drawing";
import { displayControlRef } from "../lib/controlRef";
import { fmtDate } from "../lib/format";

export type Coverage = "full" | "partial" | "none";

export interface QualityFinding { code: string; severity: "concern" | "note"; title: string; detail: string }
export interface TagCoverage { frameworkId: string; controlRef: string; coverage: Coverage; reason: string }

export interface QualityReview {
  status: "queued" | "running" | "done" | "error";
  documentVersion: number;
  findings: QualityFinding[];
  aiStatus: string;
  aiStatusText: string;
  coverage: TagCoverage[];
  pointCount: number;
  reviewedAt: string | null;
  dismissed: { at: string; byName: string | null; reason: string | null } | null;
}

export interface QualityReviewSettings { aiEnabled: boolean; aiAvailable: boolean }

export const COVERAGE_TEXT: Record<Coverage, string> = { full: "Covers it", partial: "Partly covers it", none: "Does not cover it" };
export const COVERAGE_TONE: Record<Coverage, StatusTone> = { full: "met", partial: "partial", none: "gap" };

/** The one-line chip beside a document's name. */
export function QualityChip({ review }: { review: QualityReview | null | undefined }) {
  if (!review) return null;
  if (review.status === "queued" || review.status === "running") return <StatusTag tone="none" title="The quality review runs shortly after filing">Review pending</StatusTag>;
  if (review.status === "error") return null;
  if (review.dismissed) return <StatusTag tone="none" title="Someone dismissed the review of this version">Review dismissed</StatusTag>;
  if (review.pointCount === 0) return <StatusTag tone="met" title="Advice only: nothing in the review needs a look">Reviewed</StatusTag>;
  return (
    <StatusTag tone="partial" title="Advice only: the review found points to check. It changes no tag or status.">
      {review.pointCount} point{review.pointCount === 1 ? "" : "s"} to check
    </StatusTag>
  );
}

const sub: React.CSSProperties = { fontSize: 11, color: theme.color.subtle };

/**
 * The review in full, opened from the document card: rule findings, per-tag coverage and the
 * dismiss / restore controls. `onChange` reloads the list after a dismiss.
 */
export function QualityReviewPanel({
  documentId, review, tags, canWrite, onChange,
}: {
  documentId: string;
  review: QualityReview | null | undefined;
  tags: Array<{ frameworkId: string; controlRef: string }>;
  canWrite: boolean;
  onChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (path: string, body?: unknown) => {
    setBusy(true); setError(null);
    try {
      await api.post(`/documents/${encodeURIComponent(documentId)}/${path}`, body ?? {});
      setDismissing(false); setReason("");
      onChange();
    } catch (e) {
      setError(apiErrorMessage(e, "That did not work. Try again."));
    } finally {
      setBusy(false);
    }
  };

  if (!review) {
    return canWrite ? (
      <div style={{ ...sub, marginTop: 6 }}>
        Not reviewed.{" "}
        <button type="button" disabled={busy} onClick={() => act("quality-review")} style={linkBtn}>Review this version</button>
        {error && <span style={{ color: theme.color.dangerText, marginLeft: 6 }}>{error}</span>}
      </div>
    ) : null;
  }
  if (review.status === "queued" || review.status === "running") {
    return <div style={{ ...sub, marginTop: 6 }}>Quality review in progress. It never changes a tag or a status.</div>;
  }
  if (review.status === "error") {
    return <div style={{ ...sub, marginTop: 6 }}>The quality review of this version could not be completed.</div>;
  }

  const byTag = new Map(review.coverage.map((c) => [`${c.frameworkId}:${c.controlRef}`, c]));
  const notes = review.findings;

  return (
    <div style={{ marginTop: 6 }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={linkBtn} aria-expanded={open}>
        {open ? "Hide quality review" : review.dismissed ? "Quality review (dismissed)" : `Quality review of v${review.documentVersion}${review.pointCount ? ` · ${review.pointCount} point${review.pointCount === 1 ? "" : "s"} to check` : ""}`}
      </button>
      {open && (
        <div style={{ marginTop: 6, padding: "10px 12px", border: `1px solid ${theme.color.line}`, borderRadius: 3, background: theme.color.panel, display: "grid", gap: 8 }}>
          <div style={sub}>
            Advice on whether this document evidences the controls it is tagged to. It never removes a tag, changes a status or stops the document counting.
            {review.reviewedAt ? ` Reviewed ${fmtDate(review.reviewedAt)}.` : ""}
          </div>

          {notes.length > 0 && (
            <div style={{ display: "grid", gap: 4 }}>
              {notes.map((f) => (
                <div key={f.code} style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  <StatusTag tone={f.severity === "concern" ? "partial" : "none"}>{f.severity === "concern" ? "Check" : "Note"}</StatusTag>
                  <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.text }}>{f.title}</span>
                  <span style={sub}>{f.detail}</span>
                </div>
              ))}
            </div>
          )}

          {tags.length > 0 && (
            <div style={{ display: "grid", gap: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.muted }}>Against each tagged control</div>
              {tags.map((t) => {
                const c = byTag.get(`${t.frameworkId}:${t.controlRef}`);
                return (
                  <div key={`${t.frameworkId}:${t.controlRef}`} style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: theme.color.text, minWidth: 90 }}>{displayControlRef(t.controlRef)}</span>
                    {c ? <StatusTag tone={COVERAGE_TONE[c.coverage]}>{COVERAGE_TEXT[c.coverage]}</StatusTag> : <StatusTag tone="none">Not assessed</StatusTag>}
                    <span style={sub}>{c ? c.reason : review.aiStatus === "done" ? "Tagged after the review, or not answered." : review.aiStatusText}</span>
                  </div>
                );
              })}
            </div>
          )}

          {notes.length === 0 && review.coverage.length === 0 && <div style={sub}>{review.aiStatusText || "Nothing to report."}</div>}

          {review.dismissed ? (
            <div style={{ ...sub, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
              <span>Dismissed {fmtDate(review.dismissed.at)}{review.dismissed.byName ? ` by ${review.dismissed.byName}` : ""}{review.dismissed.reason ? `: ${review.dismissed.reason}` : ""}.</span>
              {canWrite && <button type="button" disabled={busy} onClick={() => act("quality-review/restore")} style={linkBtn}>Restore</button>}
            </div>
          ) : canWrite && (
            dismissing ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                <input
                  aria-label="Why the review does not apply (optional)" placeholder="Why it does not apply (optional)" maxLength={500}
                  value={reason} onChange={(e) => setReason(e.target.value)} style={{ ...inputStyle, flex: "1 1 240px", fontSize: 12 }}
                />
                <button type="button" disabled={busy} onClick={() => act("quality-review/dismiss", { reason: reason.trim() || undefined })} style={btnSecondary}>Dismiss review</button>
                <button type="button" disabled={busy} onClick={() => setDismissing(false)} style={linkBtn}>Cancel</button>
              </div>
            ) : (
              <div><button type="button" onClick={() => setDismissing(true)} style={linkBtn}>Dismiss this review</button></div>
            )
          )}
          {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
        </div>
      )}
    </div>
  );
}

/** The organisation's switch for the AI part (org:admin on the server). */
export function QualityReviewSettingsLine({ settings, canAdmin, onChange }: { settings: QualityReviewSettings | null; canAdmin: boolean; onChange: (s: QualityReviewSettings) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!settings) return null;
  const toggle = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.patch<{ aiEnabled: boolean }>("/documents/quality-review-settings", { aiEnabled: !settings.aiEnabled });
      onChange({ ...settings, aiEnabled: r.aiEnabled });
    } catch (e) {
      setError(apiErrorMessage(e, "The setting could not be saved."));
    } finally {
      setBusy(false);
    }
  };
  const state = !settings.aiAvailable
    ? "Each document filed is checked by rule (readable text, draft markings, sign-off, dates). The AI review of each tagged control needs the Self-Service + AI tier."
    : settings.aiEnabled
      ? "Each document filed is checked by rule, then the AI says whether it covers each control it is tagged to. Advice only."
      : "Each document filed is checked by rule. The AI review is switched off, so no document text is sent to a model for it.";
  return (
    <div style={{ ...sub, display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", margin: "4px 0 8px" }}>
      <span>{state}</span>
      {canAdmin && settings.aiAvailable && (
        <button type="button" disabled={busy} onClick={toggle} style={linkBtn}>{settings.aiEnabled ? "Switch the AI review off" : "Switch the AI review on"}</button>
      )}
      {error && <span style={{ color: theme.color.dangerText }}>{error}</span>}
    </div>
  );
}

const linkBtn: React.CSSProperties = {
  background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: theme.color.accentText, fontFamily: theme.font,
};
